import {
  DATASET_TRIGGER_CRITERION_LABELS,
  type DatasetTriggerCriterion,
  type DatasetTriggerRunMode,
} from '@cotera/sdk/core';

/**
 * Everything naming a dataset trigger depends on. Deliberately not the trigger
 * row type: the creation wizard names a trigger that does not exist yet, and
 * both callers must arrive at the same answer from the same facts.
 */
export type DatasetTriggerNamingFacts = {
  /** Null while the dataset's name is still loading. */
  datasetName: string | null;
  criterion: DatasetTriggerCriterion | null;
  runMode: DatasetTriggerRunMode | null;
};

/**
 * What a dataset trigger *is*, in one line — "Customers · A new row is added".
 *
 * The dataset it points at is the only thing that identifies it, so the name
 * leads with that and then says what it watches for. Degrades a part at a time:
 * a bare criterion while the name loads, and a fixed phrase if neither is known
 * yet, so the row never renders as empty.
 */
export function datasetTriggerDescription(
  facts: DatasetTriggerNamingFacts
): string {
  const parts = [
    facts.datasetName !== null && facts.datasetName !== ''
      ? facts.datasetName
      : null,
    facts.criterion !== null
      ? DATASET_TRIGGER_CRITERION_LABELS[facts.criterion]
      : null,
    // Last, and only when it is the surprising answer. A trigger that never
    // fires on its own looks broken in a list of triggers unless it says so.
    facts.runMode === 'manual' ? 'manual only' : null,
  ].filter((part): part is string => part !== null);

  return parts.length > 0 ? parts.join(' · ') : 'Watches a dataset';
}
